import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { mfaApi } from "@/lib/api";
import { ShieldCheck, Smartphone, Copy, ArrowRight, KeyRound, Loader2 } from "lucide-react";

/** Paso de código dinámico en el login (segundo factor ya configurado). */
export function MfaCodeStep({ pendingToken, email, onDone, onCancel }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await mfaApi.loginCode(pendingToken, code.trim());
      onDone(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Código incorrecto");
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="mfa-code-step">
      <div className="flex items-start gap-3 rounded-2xl bg-slate-900 text-white p-4">
        <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0 text-emerald-400" />
        <div>
          <p className="font-bold">Clave dinámica</p>
          <p className="text-sm text-slate-300 mt-0.5">
            Abre tu app de autenticación y escribe el código de 6 dígitos de <strong>{email}</strong>.
          </p>
        </div>
      </div>
      <div>
        <Label htmlFor="mfa-code">Código de 6 dígitos</Label>
        <Input
          id="mfa-code" data-testid="mfa-code-input" autoFocus autoComplete="one-time-code"
          inputMode="text" maxLength={24} value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="123456"
          className="mt-1.5 h-12 rounded-xl text-center text-xl tracking-[0.35em] font-mono-num"
        />
        <p className="text-xs text-slate-500 mt-2">
          Perdiste el teléfono? Escribe uno de tus códigos de respaldo.
        </p>
      </div>
      <Button type="submit" data-testid="mfa-code-submit" disabled={busy || code.trim().length < 6}
              className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando…</> : <>Verificar e ingresar <ArrowRight className="w-4 h-4 ml-1" /></>}
      </Button>
      <button type="button" data-testid="mfa-code-cancel" onClick={onCancel}
              className="w-full text-sm text-slate-500 hover:text-slate-800">
        Usar otra cuenta
      </button>
    </form>
  );
}

/** Paso de enrolamiento: QR + confirmación + códigos de respaldo. */
export function MfaSetupStep({ setupToken, email, onDone }) {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);
  const [pending, setPending] = useState(null);

  const begin = async () => {
    setBusy(true);
    try {
      setData(await mfaApi.start(setupToken));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar el código QR");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await mfaApi.confirm(code.trim(), setupToken);
      setCodes(r.recovery_codes);
      setPending(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "El código no coincide");
    } finally {
      setBusy(false);
    }
  };

  if (codes) {
    return (
      <div className="space-y-5" data-testid="mfa-recovery-codes">
        <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4">
          <p className="font-bold text-emerald-900">Clave dinámica activada</p>
          <p className="text-sm text-emerald-800 mt-1">
            Guarda estos 10 códigos de respaldo en un lugar seguro. Cada uno sirve una sola vez y
            no se vuelven a mostrar.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {codes.map((c) => (
            <code key={c} className="rounded-xl bg-slate-900 text-white px-3 py-2 text-center text-sm font-mono-num tracking-wide">
              {c}
            </code>
          ))}
        </div>
        <Button type="button" variant="outline" data-testid="mfa-copy-codes"
                onClick={() => { navigator.clipboard?.writeText(codes.join("\n")); toast.success("Códigos copiados"); }}
                className="w-full h-11 rounded-xl">
          <Copy className="w-4 h-4 mr-2" /> Copiar los 10 códigos
        </Button>
        <Button type="button" data-testid="mfa-setup-finish" onClick={() => onDone(pending)}
                className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold">
          Ya los guardé, entrar al panel <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="mfa-setup-step">
      <div className="flex items-start gap-3 rounded-2xl bg-slate-900 text-white p-4">
        <Smartphone className="w-5 h-5 mt-0.5 shrink-0 text-sky-400" />
        <div>
          <p className="font-bold">Activa tu clave dinámica</p>
          <p className="text-sm text-slate-300 mt-0.5">
            Tu cuenta <strong>{email}</strong> administra toda la plataforma, así que además de la
            contraseña necesita un código que cambia cada 30 segundos.
          </p>
        </div>
      </div>

      {!data ? (
        <>
          <ul className="text-sm text-slate-600 space-y-1.5 list-disc pl-5">
            <li>Google Authenticator</li>
            <li>Microsoft Authenticator</li>
            <li>Contraseñas de iPhone (iOS 18 o superior)</li>
          </ul>
          <Button type="button" data-testid="mfa-setup-start" onClick={begin} disabled={busy}
                  className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando…</> : <><KeyRound className="w-4 h-4 mr-2" /> Mostrar el código QR</>}
          </Button>
        </>
      ) : (
        <form onSubmit={confirm} className="space-y-4">
          <div className="rounded-2xl border border-slate-200 p-4 flex flex-col items-center">
            <img src={data.qr_data_uri} alt="Código QR para la app de autenticación"
                 data-testid="mfa-setup-qr" className="w-44 h-44" />
            <p className="text-xs text-slate-500 mt-3 text-center">
              No puedes escanear? Escribe esta clave en la app:
            </p>
            <button type="button" data-testid="mfa-setup-secret"
                    onClick={() => { navigator.clipboard?.writeText(data.secret_manual); toast.success("Clave copiada"); }}
                    className="mt-1 text-xs font-mono-num font-bold tracking-wider text-slate-800 break-all hover:text-sky-700">
              {data.secret_manual}
            </button>
          </div>
          <div>
            <Label htmlFor="mfa-confirm">Escribe el código que muestra la app</Label>
            <Input
              id="mfa-confirm" data-testid="mfa-setup-code" autoFocus autoComplete="one-time-code"
              inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className="mt-1.5 h-12 rounded-xl text-center text-xl tracking-[0.35em] font-mono-num"
            />
          </div>
          <Button type="submit" data-testid="mfa-setup-confirm" disabled={busy || code.length !== 6}
                  className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activando…</> : <>Activar clave dinámica <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </form>
      )}
    </div>
  );
}
